import React from 'react';
import AirportSuggestionSearch from './AirportSuggestionSearch';
import { airportSearchEmptyMessage } from './flightForm.utils';

/**
 * From/To Autocomplete listbox: in-panel search plus airport rows.
 * MUI passes a listbox ref — use the default export (forwardRef) as ListboxComponent.
 */
class AirportSuggestionListboxView extends React.Component {
  componentDidMount() {
    this.focusSearch();
  }

  componentDidUpdate() {
    this.focusSearch();
  }

  focusSearch = () => {
    if (typeof this.props.onMountFocus === 'function') this.props.onMountFocus();
  };

  handleQueryChange = (value) => {
    if (typeof this.props.onQueryChange === 'function') this.props.onQueryChange(value);
  };

  render() {
    const {
      children,
      searchHostClassName,
      inputRef,
      query,
      loading,
      listboxRef,
      onQueryChange,
      onMountFocus,
      ...other
    } = this.props;
    const hasResults = React.Children.toArray(children).some(Boolean);
    const emptyMessage = airportSearchEmptyMessage(hasResults, loading);

    return (
      <ul
        {...other}
        ref={listboxRef}
        style={{ ...other.style, padding: 0, paddingTop: 0, margin: 0 }}
      >
        <li
          role="presentation"
          className={searchHostClassName}
          style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '8px' }}
        >
          <AirportSuggestionSearch
            inputRef={inputRef}
            value={query}
            onChange={this.handleQueryChange}
          />
        </li>
        {emptyMessage ? (
          <li className="flight-dropdown-empty" role="presentation">{emptyMessage}</li>
        ) : children}
      </ul>
    );
  }
}

const AirportSuggestionListbox = React.forwardRef(function AirportSuggestionListbox(props, ref) {
  return <AirportSuggestionListboxView {...props} listboxRef={ref} />;
});

/**
 * Stable Autocomplete ListboxComponent that reads From/To search state from a ctx ref.
 */
export function createAirportSuggestionListbox({
  ctxRef,
  inputRef,
  searchHostClassName,
  field,
  onMountFocus,
}) {
  return React.forwardRef(function BoundAirportSuggestionListbox(props, ref) {
    const ctx = ctxRef.current || {};
    const useSplit = ctx.isDesktopFromSplit;
    const isFrom = field === 'from';
    const query = useSplit
      ? (isFrom ? ctx.fromSearchQuery : ctx.toSearchQuery)
      : (isFrom ? ctx.fromInput : ctx.toInput);
    const setQuery = useSplit
      ? (isFrom ? ctx.setFromSearchQuery : ctx.setToSearchQuery)
      : (isFrom ? ctx.setFromInput : ctx.setToInput);
    const setAirport = isFrom ? ctx.setFromAirport : ctx.setToAirport;
    const loading = isFrom ? ctx.fromLoading : ctx.toLoading;

    const handleQueryChange = (value) => {
      if (typeof setQuery === 'function') setQuery(value);
      if ((!value || value.trim() === '') && typeof setAirport === 'function') {
        setAirport(null);
      }
    };

    return (
      <AirportSuggestionListboxView
        {...props}
        listboxRef={ref}
        searchHostClassName={searchHostClassName}
        inputRef={inputRef}
        query={query}
        loading={loading}
        onQueryChange={handleQueryChange}
        onMountFocus={onMountFocus}
      />
    );
  });
}

export default AirportSuggestionListbox;
